import React, { useEffect } from "react";
import { Common, Icon } from "@/components";
import { Address, GoogleAddressPrediction } from "../models";
import { isEmptyAddress, isMissingAddressComponents } from "@/utils/address";
import ReactTooltip from "react-tooltip";

export interface AddressWithStatus {
  customerName: string;
  address: string;
  fixedAddress?: Address;
  isFixed?: boolean;
  hasSuggestion?: boolean;
  docNumbers: string[];
  isGeocoding?: boolean;
}

const MissingAddressComponentsWarning = () => {
  const tooltip =
    "This address may not be specific enough for delivery. Please review and add missing details if possible.";

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  return (
    <div className="ml-2 text-neutral-50 cursor-help" data-tip={tooltip}>
      <Icon.WarningCircle className="fill-yellow" size="16" />
    </div>
  );
};

const NoSuggestionsWarning = () => {
  const tooltip =
    "We could not find any suggestions for this address. Please review and add missing details if possible.";

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  return (
    <div className="ml-2 text-neutral-50 cursor-help" data-tip={tooltip}>
      <Icon.WarningCircle className="fill-red" size="16" />
    </div>
  );
};

const EmptyAddressWarning = () => {
  const tooltip =
    "This address is empty. Please review and fill the correct address.";

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  return (
    <div className="ml-2 text-neutral-50 cursor-help" data-tip={tooltip}>
      <Icon.WarningCircle className="fill-red" size="16" />
    </div>
  );
};

interface AddressTableProps {
  addresses: AddressWithStatus[];
  showSuggestions: AddressWithStatus | null;
  addressSuggestions: GoogleAddressPrediction[];
  isLoadingSuggestions: boolean;
  handleAddressChange: (
    address: AddressWithStatus,
    fixedAddress: string
  ) => void;
  handleSelectSuggestion: (
    address: AddressWithStatus,
    prediction: GoogleAddressPrediction
  ) => void;
  handleApproveAddress: (address: AddressWithStatus) => void;
  handleOpenMap: (address: AddressWithStatus) => void;
}

const AddressTable: React.FC<AddressTableProps> = ({
  addresses,
  showSuggestions,
  addressSuggestions,
  isLoadingSuggestions,
  handleAddressChange,
  handleSelectSuggestion,
  handleApproveAddress,
  handleOpenMap,
}) => {
  return (
    <div className="relative w-full border border-line border-separate border-spacing-0">
      <table className="w-full">
        <thead>
          <tr>
            <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
              Customer Name
            </th>
            <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
              Invalid Address
            </th>
            <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left w-[450px]">
              Correct Address
            </th>
            <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-center">
              Actions
            </th>
            <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-center">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {addresses.length > 0 ? (
            addresses.map((address) => (
              <tr
                key={address.customerName + address.address}
                className="hover:bg-neutral-5"
              >
                <td className="px-4 py-2 border-b border-neutral-10">
                  {address.customerName}
                </td>
                <td className="px-4 py-2 border-b border-neutral-10">
                  {address.address}
                </td>
                <td className="px-4 py-2 border-b border-neutral-10 w-[450px] relative">
                  <Common.Input
                    type="text"
                    className="w-full"
                    iconRight={
                      address.isGeocoding
                        ? Icon.Loading
                        : isEmptyAddress(address.fixedAddress) &&
                          !address.fixedAddress?.fullAddress
                        ? address.hasSuggestion
                          ? EmptyAddressWarning
                          : NoSuggestionsWarning
                        : address.fixedAddress &&
                          isMissingAddressComponents(address.fixedAddress) &&
                          showSuggestions?.address !== address.address &&
                          showSuggestions?.customerName !== address.customerName
                        ? MissingAddressComponentsWarning
                        : undefined
                    }
                    value={address.fixedAddress?.fullAddress || ""}
                    onChange={(e) =>
                      handleAddressChange(address, e.target.value)
                    }
                    placeholder="Enter correct address"
                    disabled={address.isGeocoding}
                  />

                  {/* Address suggestions dropdown */}
                  {showSuggestions?.customerName === address.customerName &&
                    showSuggestions?.address === address.address &&
                    addressSuggestions.length > 0 && (
                      <div className="absolute left-0 right-0 z-10 mt-1 bg-white border border-neutral-20 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {isLoadingSuggestions ? (
                          <div className="p-4 text-center">
                            <Icon.Loading size="16" className="inline mr-2" />
                            Loading suggestions...
                          </div>
                        ) : (
                          addressSuggestions.map((suggestion) => (
                            <div
                              key={suggestion.place_id}
                              className="px-4 py-2 hover:bg-neutral-5 cursor-pointer"
                              onClick={() =>
                                handleSelectSuggestion(address, suggestion)
                              }
                            >
                              {suggestion.description}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                </td>
                <td className="px-4 py-2 border-b border-neutral-10 text-center">
                  <div className="flex justify-center space-x-2">
                    {address.hasSuggestion &&
                      !address.isFixed &&
                      !address.isGeocoding &&
                      !isEmptyAddress(address.fixedAddress) && (
                        <Common.Button
                          onClick={() => handleApproveAddress(address)}
                          color="blue"
                          size="sm"
                          iconLeft={Icon.Checked}
                        >
                          Approve
                        </Common.Button>
                      )}
                    <Common.Button
                      onClick={() => handleOpenMap(address)}
                      color="blue"
                      size="sm"
                      iconLeft={Icon.Location}
                    >
                      Pin Map
                    </Common.Button>
                  </div>
                </td>
                <td className="px-4 py-2 border-b border-neutral-10 text-center">
                  {address.isFixed ? (
                    <Icon.CheckedCircle
                      size="24"
                      className="fill-green-500 inline-block"
                    />
                  ) : (
                    <span className="text-neutral-50 whitespace-nowrap">
                      Not fixed
                    </span>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="p-4 text-center text-neutral-50">
                No addresses to fix
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AddressTable;
